import { IconSizes } from "atoms/constants/icon-sizes";
import { UserBookmarkCountBy } from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-by";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import React from "react";
import useActiveBookmarkGroups from "utilities/hooks/domain/my-link/use-active-bookmark-groups";
import AccordionBookmarkGrid from "./accordions/accordion-bookmark-grid";
import useBookmarksInPanelQuery from "utilities/hooks/queries/my-link/bookmarks/grouped/use-bookmarks-in-panel-query";
import useLazyLoadBookmarks from "utilities/hooks/domain/my-link/use-lazy-load-bookmarks";
import UserBookmarkRecord from "models/view-models/user-bookmark-record";
import { BookmarkPaginationSettings } from "constants/bookmark-pagination-constants";
import useSortBookmarks from "utilities/hooks/domain/my-link/use-sort-bookmarks";
import useSearchBookmarks from "utilities/hooks/domain/my-link/use-search-bookmarks";
import useFilterBookmarks from "utilities/hooks/domain/my-link/use-filter-bookmarks";
import UserBookmarkShareFilterOption from "models/enumerations/filtering/user-bookmarks/user-bookmark-share-filter-option";
import EmptyMyLinkPanel from "organisms/my-link/empty-mylink-panel";

interface MyBookmarksAccordionPanelProps {
    header: JSX.Element;
    id: string | number;
    groupBy: UserBookmarkCountBy;
    filterBookmarksBySearch?: boolean;
    gridColumn?: number;
    onBookmarkClick?: (bookmark: UserBookmarkRecord) => void;
}

const CSS_CLASS_NAME = "c-my-bookmarks-accordion-panel";

const DEFAULT_TAKE_SIZE = BookmarkPaginationSettings.AccordionTake;

const MyBookmarksAccordionPanel = (props: MyBookmarksAccordionPanelProps) => {
    const {
        header,
        groupBy,
        id,
        filterBookmarksBySearch,
        gridColumn,
        onBookmarkClick,
    } = props;
    const { onHideGroup, onShowGroup, isOpen } =
        useActiveBookmarkGroups(groupBy);

    const { sortBy } = useSortBookmarks(groupBy);
    const { searchText } = useSearchBookmarks(groupBy);
    const { shareFilter } = useFilterBookmarks(groupBy);

    const { bookmarks, isLoading, fetchNextPage, hasNextPage } =
        useBookmarksInPanelQuery({
            groupId: groupBy,
            id,
            options: {
                take: DEFAULT_TAKE_SIZE,
                sortBy,
                searchText: filterBookmarksBySearch ? searchText : undefined,
                currentUserOnly:
                    shareFilter === UserBookmarkShareFilterOption.PrivateOnly,
            },
            isOpen: isOpen(id),
        });

    const { observerRef } = useLazyLoadBookmarks({
        isLoading,
        onPageNext: fetchNextPage,
        noRecordsRemaining: !hasNextPage,
    });

    return (
        <CollapsePanel
            iconSize={IconSizes.Base}
            modifierCSSClassName={CSS_CLASS_NAME}
            buttonAriaText={""}
            onOpen={() => {
                onShowGroup(id);
            }}
            onClose={() => onHideGroup(id)}
            collapse={!isOpen(id)}
            panelTop={header}>
            {!isLoading &&
            bookmarks?.pages.flatMap((page) => page?.bookmarks).length === 0 ? (
                <EmptyMyLinkPanel />
            ) : (
                <AccordionBookmarkGrid
                    gridColumn={gridColumn}
                    isLoading={isLoading}
                    bookmarks={
                        bookmarks?.pages.flatMap(
                            (page) => page?.bookmarks
                        ) as UserBookmarkRecord[]
                    }
                    groupBy={groupBy}
                    panelId={id}
                    observerRef={observerRef}
                    hasRecordsRemaining={hasNextPage ?? false}
                    onClick={onBookmarkClick}
                />
            )}
        </CollapsePanel>
    );
};
export default MyBookmarksAccordionPanel;
